import { Radio as RadioPrimitive } from '@base-ui/react/radio';
import { RadioGroup as RadioGroupPrimitive } from '@base-ui/react/radio-group';
import { ReactNode } from 'react';
import { cn } from 'utils/cn';

function RadioGroup({ className, ...props }: RadioGroupPrimitive.Props) {
    return <RadioGroupPrimitive data-slot="radio-group" className={cn('grid w-full gap-3', className)} {...props} />;
}

// Drawn as the app's brand radio: a grey ring at rest; checked, an orange ring around a lighter orange dot.
// `variant="primary"` checks it green instead, ring and dot, for the screens that pair it with the green checkbox.
// The new design checks every radio green whatever the variant, in each state that names the checked ring.
function RadioGroupItem({
    className,
    variant = 'secondary',
    ...props
}: RadioPrimitive.Root.Props & { variant?: 'primary' | 'secondary' }) {
    return (
        <RadioPrimitive.Root
            data-slot="radio-group-item"
            className={cn(
                'group/radio-group-item peer border-grey-500 group-has-[:focus-visible]/field-label:not-data-checked:border-grey-500 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 aria-invalid:aria-checked:border-secondary dark:bg-input/30 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 data-checked:border-secondary group-has-[:focus-visible]/field-label:data-checked:border-secondary new:data-checked:border-primary new:group-has-[:focus-visible]/field-label:data-checked:border-primary new:aria-invalid:aria-checked:border-primary dark:data-checked:bg-primary relative flex aspect-square size-4 shrink-0 rounded-full border outline-none group-has-[:focus-visible]/field-label:ring-0 after:absolute after:-inset-x-3 after:-inset-y-2 focus-visible:ring-3 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-3',
                variant === 'primary' &&
                    'aria-invalid:aria-checked:border-primary data-checked:border-primary group-has-[:focus-visible]/field-label:data-checked:border-primary',
                className
            )}
            {...props}
        >
            <RadioPrimitive.Indicator
                data-slot="radio-group-indicator"
                className="flex size-4 items-center justify-center"
            >
                <span
                    className={cn(
                        'absolute top-1/2 left-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full',
                        variant === 'primary' ? 'bg-primary' : 'bg-secondary-light new:bg-primary'
                    )}
                />
            </RadioPrimitive.Indicator>
        </RadioPrimitive.Root>
    );
}

// An option as a card: the radio beside a title over a description, inside a border that colours while it is checked.
function RadioCard({
    title,
    description,
    disabled,
    className,
    ...props
}: RadioPrimitive.Root.Props & { title: ReactNode; description?: ReactNode }) {
    return (
        <label
            className={cn(
                // The menu border and the wash exist only in the new design; classic draws its own border and a tint of primary.
                'border-border new:border-border-menu has-data-checked:border-primary new:has-data-checked:border-primary has-data-checked:bg-primary/5 new:has-data-checked:bg-primary-wash flex items-start gap-2.5 rounded-md border-[1.5px] px-3.5 py-3',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                className
            )}
        >
            <RadioGroupItem disabled={disabled} className="mt-0.5" {...props} />
            <span className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{title}</span>
                {description ? <span className="text-foreground-secondary text-meta">{description}</span> : null}
            </span>
        </label>
    );
}

export { RadioCard, RadioGroup, RadioGroupItem };
